import React, { useState } from 'react';
import { useAudioRecorder } from '../../hooks/useAudioRecorder';
import { Visualizer } from './Visualizer';
import { formatTime } from '../../utils/audioHelpers';
import { Mic, Square, Pause, Play, Download, Trash2, Share2, Copy, Check, Loader2 } from 'lucide-react';
import { clsx } from 'clsx';
import { client } from '../../utils/client';
import { motion, AnimatePresence } from 'framer-motion';

export const Recorder: React.FC = () => {
  const {
    isRecording,
    isPaused,
    recordingTime,
    mediaBlob,
    mediaUrl,
    startRecording,
    stopRecording,
    togglePause,
    resetRecording,
    getVisualizerData,
  } = useAudioRecorder();

  const [shareLink, setShareLink] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleDownloadWav = () => {
    if (mediaUrl) {
      const a = document.createElement('a');
      a.href = mediaUrl;
      a.download = `recording-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.wav`;
      a.click();
    }
  };

  const handleShare = async () => {
    if (!mediaBlob) return;
    
    setIsUploading(true);
    setShareLink(null);
    try {
      const blobToUpload = mediaBlob;
      const ext = 'wav';
      const filename = `recording-${Date.now()}.${ext}`;
      
      // 1. Get presigned URL
      const res = await client.api.fetch('/api/public/upload/presign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename, contentType: blobToUpload?.type })
      });
      
      if (!res.ok) throw new Error('Failed to get upload URL');
      const { uploadUrl, path } = await res.json();
      
      // 2. Upload to S3
      await fetch(uploadUrl, {
        method: 'PUT',
        body: blobToUpload
      });
      
      // 3. Generate share link
      const workerUrl = "https://staging--odmogakqpkdt4p1v70ne.youbase.cloud";
      const link = `${workerUrl}/api/public/share?path=${encodeURIComponent(path)}`;
      setShareLink(link);
      
    } catch (error) {
      console.error(error);
      alert('Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const copyToClipboard = () => {
    if (shareLink) {
      navigator.clipboard.writeText(shareLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleReset = () => {
    setShareLink(null);
    setCopied(false);
    resetRecording();
  };

  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
      className="w-full max-w-sm mx-auto backdrop-blur-xl bg-white/80 border border-white/40 shadow-2xl rounded-3xl overflow-hidden"
    >
      <div className="p-6">
        {/* Header & Timer */}
        <div className="flex flex-col items-center justify-center mb-6 bg-blue-600 p-4 rounded-2xl shadow-inner w-full">
          <div className="text-sm font-medium text-white/90 uppercase tracking-widest mb-2">
            {isRecording ? (isPaused ? 'Paused' : 'Recording') : (mediaUrl ? 'Recorded' : 'Ready')}
          </div>
          <div className="text-5xl font-light font-mono tabular-nums transition-colors duration-300 text-white">
            {formatTime(recordingTime)}
          </div>
        </div>

        {/* Visualizer Area */}
        <div className="mb-6 h-24 bg-gray-50/50 rounded-2xl border border-gray-100 overflow-hidden relative">
          <Visualizer getVisualizerData={getVisualizerData} isRecording={isRecording && !isPaused} />
          {!isRecording && !mediaUrl && (
            <div className="absolute inset-0 flex items-center justify-center text-gray-300 text-sm"></div>
          )}
        </div>

        {/* Controls */}
        <div className="flex flex-col items-center justify-center gap-4">
          <AnimatePresence mode="wait">
            {!isRecording && !mediaUrl && (
              <motion.button
                key="start"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={startRecording}
                className="group relative flex items-center justify-center w-20 h-20 bg-blue-600 text-white rounded-full shadow-xl hover:shadow-2xl transition-all"
              >
                <div className="absolute inset-0 rounded-full border border-blue-600 opacity-20 group-hover:scale-110 transition-transform duration-500" />
                <Mic className="w-8 h-8 text-white" />
              </motion.button>
            )}

            {isRecording && (
              <motion.div 
                key="recording-controls"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                className="flex items-center gap-6"
              >
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={togglePause}
                  className={clsx(
                    "p-5 rounded-full transition-colors shadow-lg",
                    isPaused ? "bg-blue-50 text-blue-600" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  )}
                >
                  {isPaused ? <Play className="w-6 h-6 fill-current" /> : <Pause className="w-6 h-6 fill-current" />}
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={stopRecording}
                  className="p-5 bg-red-500 text-white rounded-full shadow-lg hover:bg-red-600 hover:shadow-red-500/30 transition-colors"
                >
                  <Square className="w-6 h-6 fill-current" />
                </motion.button>
              </motion.div>
            )}

            {mediaUrl && (
              <motion.div 
                key="playback-controls"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full space-y-6"
              >
                <audio src={mediaUrl} controls className="w-full h-10 rounded-lg opacity-80 hover:opacity-100 transition-opacity" />
                
                {shareLink ? (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="bg-blue-50/50 p-4 rounded-xl border border-blue-100"
                  >
                    <p className="text-xs font-semibold text-blue-800 uppercase tracking-wider mb-2">Share Link Ready</p>
                    <div className="flex gap-2">
                      <input 
                        type="text" 
                        readOnly 
                        value={shareLink} 
                        className="flex-1 text-sm p-3 border border-blue-200 rounded-lg bg-white/80 text-gray-600 select-all focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                      />
                      <motion.button 
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={copyToClipboard}
                        className="p-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-md"
                      >
                        {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                      </motion.button>
                    </div>
                  </motion.div>
                ) : (
                  <div className="flex items-center justify-center gap-3 w-full">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleReset}
                        className="p-3 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                        title="Discard"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
                          <path d="M3 6h18" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" style={{ fill: '#9a1111', stroke: 'none' }} />
                          <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                          <line x1="10" x2="10" y1="11" y2="17" />
                          <line x1="14" x2="14" y1="11" y2="17" />
                        </svg>
                      </motion.button>

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleShare}
                      disabled={isUploading}
                      style={{ transform: 'scale(0.985175)', backgroundColor: 'rgb(28, 107, 217)', color: '#ffffff' }}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border border-gray-200 rounded-xl transition-all font-medium shadow-sm disabled:opacity-50"
                    >
                      {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                      {isUploading ? 'Uploading...' : 'Share'}
                    </motion.button>

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleDownloadWav}
                      style={{ transform: 'scale(0.985175)', backgroundColor: '#1c6bda' }}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 text-white rounded-xl transition-all font-medium shadow-lg hover:shadow-xl"
                    >
                      <Download className="w-4 h-4" />
                      Download WAV
                    </motion.button>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
};
